/* cache-bust: 1778900001 */
/**
 * <cart-inquiry> - replaces the Shopify checkout button when the cart is in
 * inquiry mode. Collects name / email / phone and posts the cart snapshot to the
 * forms Worker behind the App Proxy as form_type `cart_inquiry`.
 *
 * The cart drawer replaces its own innerHTML on every quantity change and line
 * removal, so this element is destroyed and rebuilt while the customer is mid
 * form. Open state and typed values are mirrored into sessionStorage on every
 * input and restored in connectedCallback, which is what lets someone edit the
 * cart without losing what they typed. The same store carries the form across
 * the drawer and the /cart page.
 */
(function () {
  'use strict';

  if (customElements.get('cart-inquiry')) return;

  var STATE_KEY = 'sc_cart_inquiry_state';
  var STATE_TTL_MS = 2 * 60 * 60 * 1000;
  var FIELDS = ['first_name', 'last_name', 'email', 'phone_number', 'message'];
  var MIN_SUBMIT_MS = 1500;
  var TURNSTILE_TIMEOUT_MS = 8000;
  var TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
  var RETRY_429_DELAY_MS = 30000;

  function readState() {
    try {
      var raw = sessionStorage.getItem(STATE_KEY);
      if (!raw) return null;
      var s = JSON.parse(raw);
      if (!s || typeof s !== 'object') return null;
      if (!s.ts || Date.now() - s.ts > STATE_TTL_MS) return null;
      return s;
    } catch (e) {
      return null;
    }
  }
  function writeState(patch) {
    try {
      var s = readState() || { open: false, values: {} };
      if (patch.open != null) s.open = !!patch.open;
      if (patch.values) s.values = patch.values;
      s.ts = Date.now();
      sessionStorage.setItem(STATE_KEY, JSON.stringify(s));
    } catch (e) {}
  }
  function clearState() {
    try { sessionStorage.removeItem(STATE_KEY); } catch (e) {}
  }

  function toE164(raw) {
    if (!raw) return '';
    var digits = String(raw).replace(/[^\d+]/g, '');
    if (digits.indexOf('+') === 0) return digits;
    if (digits.length === 10) return '+1' + digits;
    if (digits.length === 11 && digits.charAt(0) === '1') return '+' + digits;
    return digits;
  }

  function loadTurnstileScript() {
    if (window.turnstile) return Promise.resolve();
    var existing = document.querySelector('script[data-cart-inquiry-turnstile]');
    if (!existing) {
      var s = document.createElement('script');
      s.src = TURNSTILE_SRC;
      s.async = true;
      s.defer = true;
      s.setAttribute('data-cart-inquiry-turnstile', '');
      document.head.appendChild(s);
    }
    return new Promise(function (resolve) {
      var waited = 0;
      (function poll() {
        if (window.turnstile) return resolve();
        waited += 100;
        if (waited > TURNSTILE_TIMEOUT_MS) return resolve();
        setTimeout(poll, 100);
      })();
    });
  }

  /**
   * Reads the live cart from Shopify rather than scraping the rendered markup,
   * so what the shop receives matches what is actually in the cart at submit
   * time even if a line was changed a moment earlier.
   */
  function fetchCartSnapshot() {
    return fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root + 'cart.js'
      : '/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = (cart.items || []).map(function (i) {
          return {
            variant_id: String(i.variant_id || ''),
            product_id: String(i.product_id || ''),
            title: i.product_title || i.title || '',
            variant_title: i.variant_title || '',
            sku: i.sku || '',
            vendor: i.vendor || '',
            quantity: i.quantity || 1,
            price: i.price || 0,
            line_price: i.final_line_price != null ? i.final_line_price : i.line_price || 0,
            url: i.url || '',
            image: i.image || '',
            properties: i.properties && typeof i.properties === 'object'
              ? Object.keys(i.properties)
                  .filter(function (k) { return k.charAt(0) !== '_' && i.properties[k]; })
                  .slice(0, 8)
                  .reduce(function (acc, k) { acc[String(k).slice(0, 40)] = String(i.properties[k]).slice(0, 120); return acc; }, {})
              : {}
          };
        });
        return {
          items: items,
          item_count: cart.item_count || 0,
          total_price: cart.total_price || 0,
          currency: cart.currency || '',
          token: cart.token || ''
        };
      });
  }

  class CartInquiry extends HTMLElement {
    constructor() { super(); }
  }

  CartInquiry.prototype.connectedCallback = function () {
    this.ctaEl = this.querySelector('[data-inquiry-cta]');
    this.panelEl = this.querySelector('[data-inquiry-panel]');
    this.successEl = this.querySelector('[data-inquiry-success]');
    this.statusEl = this.querySelector('[data-inquiry-status]');
    this.submitBtn = this.querySelector('[data-inquiry-submit]');
    this.submitIdle = this.querySelector('[data-submit-idle]');
    this.submitLoading = this.querySelector('[data-submit-loading]');
    this.turnstileEl = this.querySelector('[data-inquiry-turnstile]');
    this.honeypot = this.querySelector('[name="company_website"]');
    this.form = this.querySelector('form');

    this.proxyEndpoint = this.dataset.proxyEndpoint || '/apps/forms';
    this.turnstileSiteKey = this.dataset.turnstileSiteKey || '';
    this.clearCartOnSuccess = this.dataset.clearCart === 'true';
    this.adsConversionLabel = this.dataset.adsConversionLabel || '';

    if (!this.form) return;

    this._submitting = false;
    this._openedAt = 0;
    this._turnstileWidgetId = null;
    this._turnstileResolve = null;

    var self = this;
    var openBtn = this.querySelector('[data-inquiry-open]');
    if (openBtn) openBtn.addEventListener('click', function () { self.openPanel(true); });
    var closeBtn = this.querySelector('[data-inquiry-close]');
    if (closeBtn) closeBtn.addEventListener('click', function () { self.closePanel(); });

    this.form.addEventListener('submit', this._onSubmit.bind(this));
    this.form.addEventListener('input', this._onInput.bind(this));

    this._restore();
  };

  CartInquiry.prototype._restore = function () {
    var state = readState();
    if (!state) return;
    var values = state.values || {};
    for (var i = 0; i < FIELDS.length; i++) {
      var el = this.form.querySelector('[name="' + FIELDS[i] + '"]');
      if (el && values[FIELDS[i]]) el.value = values[FIELDS[i]];
    }
    if (values.message) {
      var note = this.querySelector('.cart-inquiry__note');
      if (note) note.open = true;
    }
    if (state.open) this.openPanel(false);
  };

  CartInquiry.prototype._collectValues = function () {
    var values = {};
    for (var i = 0; i < FIELDS.length; i++) {
      var el = this.form.querySelector('[name="' + FIELDS[i] + '"]');
      if (el && el.value) values[FIELDS[i]] = el.value;
    }
    return values;
  };

  CartInquiry.prototype._onInput = function (e) {
    writeState({ values: this._collectValues() });
    var t = e.target;
    if (t && t.getAttribute && t.getAttribute('aria-invalid') === 'true') {
      t.setAttribute('aria-invalid', 'false');
      var err = this.form.querySelector('[data-field-error-for="' + t.name + '"]');
      if (err) err.remove();
    }
  };

  CartInquiry.prototype.openPanel = function (focus) {
    if (this.ctaEl) this.ctaEl.hidden = true;
    if (this.panelEl) this.panelEl.hidden = false;
    this.classList.add('cart-inquiry--open');
    this._openedAt = this._openedAt || Date.now();
    writeState({ open: true, values: this._collectValues() });
    if (this.turnstileSiteKey) this._initTurnstile();
    if (focus) {
      var first = this.form.querySelector('[name="first_name"]');
      if (first) {
        try { first.focus({ preventScroll: true }); } catch (e) { first.focus(); }
        try { this.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); } catch (e) {}
      }
    }
  };

  CartInquiry.prototype.closePanel = function () {
    if (this.ctaEl) this.ctaEl.hidden = false;
    if (this.panelEl) this.panelEl.hidden = true;
    this.classList.remove('cart-inquiry--open');
    this._setStatus('');
    writeState({ open: false, values: this._collectValues() });
  };

  CartInquiry.prototype._setStatus = function (message, kind) {
    if (!this.statusEl) return;
    this.statusEl.textContent = message || '';
    this.statusEl.dataset.kind = kind || '';
  };

  CartInquiry.prototype._setSubmitting = function (on) {
    this._submitting = !!on;
    if (this.submitBtn) this.submitBtn.disabled = !!on;
    if (this.submitIdle) this.submitIdle.hidden = !!on;
    if (this.submitLoading) this.submitLoading.hidden = !on;
  };

  CartInquiry.prototype._fieldError = function (name, message) {
    var input = this.form.querySelector('[name="' + name + '"]');
    if (!input) return;
    input.setAttribute('aria-invalid', 'true');
    var err = document.createElement('div');
    err.className = 'cart-inquiry__field-error body-small';
    err.setAttribute('data-field-error-for', name);
    err.textContent = message;
    input.parentNode.appendChild(err);
  };

  CartInquiry.prototype._validate = function () {
    var ok = true;
    var get = function (n) {
      var el = this.form.querySelector('[name="' + n + '"]');
      return el ? el.value.trim() : '';
    }.bind(this);

    if (!get('first_name')) { this._fieldError('first_name', 'Please enter your first name.'); ok = false; }
    var email = get('email');
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this._fieldError('email', 'Please enter a valid email address.'); ok = false;
    }
    var phone = get('phone_number');
    if (!phone || phone.replace(/\D/g, '').length < 10) {
      this._fieldError('phone_number', 'Please enter a phone number we can reach you on.'); ok = false;
    }
    return ok;
  };

  CartInquiry.prototype._initTurnstile = function () {
    var self = this;
    if (!this.turnstileEl || this._turnstileWidgetId != null) return;
    loadTurnstileScript().then(function () {
      if (!window.turnstile || self._turnstileWidgetId != null) return;
      try {
        self._turnstileWidgetId = window.turnstile.render(self.turnstileEl, {
          sitekey: self.turnstileSiteKey,
          size: 'invisible',
          callback: function (token) {
            if (self._turnstileResolve) { self._turnstileResolve(token); self._turnstileResolve = null; }
          },
          'error-callback': function () {
            if (self._turnstileResolve) { self._turnstileResolve(''); self._turnstileResolve = null; }
          },
          'expired-callback': function () {
            if (self._turnstileWidgetId != null) {
              try { window.turnstile.reset(self._turnstileWidgetId); } catch (e) {}
            }
          }
        });
      } catch (e) {}
    });
  };

  CartInquiry.prototype._getTurnstileToken = function () {
    var self = this;
    if (!this.turnstileSiteKey) return Promise.resolve('');
    return loadTurnstileScript().then(function () {
      if (!window.turnstile) return '';
      if (self._turnstileWidgetId == null) self._initTurnstile();
      if (self._turnstileWidgetId == null) return '';
      var existing = '';
      try { existing = window.turnstile.getResponse(self._turnstileWidgetId) || ''; } catch (e) {}
      if (existing) return existing;
      return new Promise(function (resolve) {
        self._turnstileResolve = resolve;
        try { window.turnstile.execute(self._turnstileWidgetId); }
        catch (e) { resolve(''); self._turnstileResolve = null; return; }
        setTimeout(function () {
          if (self._turnstileResolve) { self._turnstileResolve(''); self._turnstileResolve = null; }
        }, TURNSTILE_TIMEOUT_MS);
      });
    });
  };

  CartInquiry.prototype._resetTurnstile = function () {
    if (this._turnstileWidgetId != null && window.turnstile) {
      try { window.turnstile.reset(this._turnstileWidgetId); } catch (e) {}
    }
  };

  CartInquiry.prototype._fireConversion = function (email, phone, snapshot, submissionId) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'dl_form_submit',
        form_name: 'cart_inquiry_v1',
        form_location: window.location.pathname,
        lead_type: 'cart_inquiry',
        value: snapshot.total_price / 100,
        currency: snapshot.currency,
        user_properties: { customer_email: email, customer_phone: phone || undefined }
      });
      if (typeof window.gtag !== 'function') return;
      window.gtag('event', 'bike_request', {
        form_location: window.location.pathname,
        item_count: snapshot.item_count,
        request_total: snapshot.total_price / 100,
        currency: snapshot.currency,
        transaction_id: submissionId || ''
      });
      if (this.adsConversionLabel) {
        window.gtag('event', 'conversion', {
          send_to: this.adsConversionLabel,
          value: 0,
          transaction_id: submissionId || ''
        });
      }
    } catch (e) {}
  };

  CartInquiry.prototype._showSuccess = function () {
    if (this.ctaEl) this.ctaEl.hidden = true;
    if (this.panelEl) this.panelEl.hidden = true;
    if (this.successEl) this.successEl.hidden = false;
    this.classList.add('cart-inquiry--done');
    clearState();
    try { this.successEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); } catch (e) {}
  };

  CartInquiry.prototype._clearCart = function () {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(root + 'cart/clear.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
    })
      .then(function () {
        return fetch(root + '?section_id=cart-icon-bubble').then(function (r) { return r.text(); });
      })
      .then(function (html) {
        var target = document.getElementById('cart-icon-bubble');
        if (!target) return;
        var parsed = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
        if (parsed) target.innerHTML = parsed.innerHTML;
      })
      .catch(function () {});
  };

  CartInquiry.prototype._applyServerErrors = function (fields) {
    if (!fields || typeof fields !== 'object') return;
    var labels = {
      first_name: 'Please enter your first name.',
      email: 'Please enter a valid email address.',
      phone_number: 'Please enter a phone number we can reach you on.',
      cart_items: 'Your cart looks empty. Add something and try again.'
    };
    for (var name in fields) {
      if (!Object.prototype.hasOwnProperty.call(fields, name)) continue;
      this._fieldError(name, labels[name] || 'Please check this field.');
    }
  };

  CartInquiry.prototype._onSubmit = function (e) {
    e.preventDefault();
    if (this._submitting) return;
    if (this.honeypot && this.honeypot.value) return;

    var prior = this.form.querySelectorAll('.cart-inquiry__field-error');
    for (var p = 0; p < prior.length; p++) prior[p].remove();

    if (!this._validate()) {
      this._setStatus('Please correct the fields above.', 'error');
      var firstErr = this.form.querySelector('[aria-invalid="true"]');
      if (firstErr) firstErr.focus();
      return;
    }

    var elapsed = this._openedAt ? Date.now() - this._openedAt : 0;
    if (elapsed > 0 && elapsed < MIN_SUBMIT_MS) return;

    var self = this;
    var emailEl = this.form.querySelector('[name="email"]');
    var phoneEl = this.form.querySelector('[name="phone_number"]');
    var email = emailEl ? emailEl.value.trim() : '';
    var phone = toE164(phoneEl ? phoneEl.value.trim() : '');

    this._setSubmitting(true);
    this._setStatus('');

    Promise.all([fetchCartSnapshot(), this._getTurnstileToken()])
      .then(function (out) {
        var snapshot = out[0];
        var token = out[1];

        if (!snapshot.items.length) {
          self._setStatus('Your cart is empty. Add something and try again.', 'error');
          self._setSubmitting(false);
          return;
        }
        if (self.turnstileSiteKey && !token) {
          self._setStatus('Could not verify you are human. Please try again.', 'error');
          self._setSubmitting(false);
          return;
        }

        var fd = new FormData(self.form);
        fd.set('cart_items', JSON.stringify(snapshot.items));
        fd.set('cart_item_count', String(snapshot.item_count));
        fd.set('cart_total', String(snapshot.total_price));
        fd.set('cart_currency', snapshot.currency || self.dataset.currency || '');
        fd.set('cart_token', snapshot.token || self.dataset.cartToken || '');
        if (token) fd.set('cf_turnstile_response', token);

        return fetch(self.proxyEndpoint, {
          method: 'POST',
          body: fd,
          headers: { Accept: 'application/json' }
        })
          .then(function (res) {
            return res.json().catch(function () { return {}; }).then(function (body) {
              return { res: res, body: body };
            });
          })
          .then(function (r) {
            var res = r.res;
            var body = r.body || {};

            if (res.status === 200 && body.ok) {
              self._fireConversion(email, phone, snapshot, body.submission_id);
              self._showSuccess();
              if (self.clearCartOnSuccess) self._clearCart();
              return;
            }
            if (res.status === 400 && body.error === 'validation_failed') {
              self._applyServerErrors(body.fields);
              self._setStatus('Please review the highlighted fields and try again.', 'error');
              self._resetTurnstile();
              return;
            }
            if (res.status === 400 && body.error === 'captcha_failed') {
              self._setStatus('Could not verify you are human. Please try again.', 'error');
              self._resetTurnstile();
              return;
            }
            if (res.status === 429) {
              self._setStatus("We've had a lot of requests just now. Please try again in a moment.", 'error');
              if (self.submitBtn) {
                self.submitBtn.disabled = true;
                setTimeout(function () { if (self.submitBtn) self.submitBtn.disabled = false; }, RETRY_429_DELAY_MS);
              }
              self._resetTurnstile();
              return;
            }
            if (res.status >= 500) {
              self._setStatus('Something went wrong on our end. Please try again or call the shop.', 'error');
              self._resetTurnstile();
              return;
            }
            self._setStatus("We couldn't send your request. Please check your details and try again.", 'error');
            self._resetTurnstile();
          });
      })
      .catch(function () {
        self._setStatus('Network error. Please try again or call the shop.', 'error');
        self._resetTurnstile();
      })
      .finally(function () {
        self._setSubmitting(false);
      });
  };

  customElements.define('cart-inquiry', CartInquiry);

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest ? e.target.closest('[data-inquiry-open-drawer]') : null;
    if (!trigger) return;
    e.preventDefault();
    writeState({ open: true });
    var notification = document.querySelector('cart-notification');
    if (notification && typeof notification.close === 'function') notification.close();
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      var inquiry = drawer.querySelector('cart-inquiry');
      if (inquiry && typeof inquiry.openPanel === 'function') inquiry.openPanel(true);
    } else {
      window.location = (window.routes && window.routes.cart_url) || '/cart';
    }
  });
})();
